// src/services/pftMergePlan.js
//
// Merging a PFT Uploads card into a patient who is already in the system.
// Firebase-free and tested (pftMergePlan.test.js).
//
// Rules (decided with the user, 2026-10):
//   * Every field is shown side by side: current value vs extracted value.
//   * An empty current field is filled automatically.
//   * When both have a value and they differ, the PERSON picks. Until they do,
//     the current value is kept: nothing is overwritten without a choice.
//   * New PFT dates are added. A PFT on a date the patient already has is
//     compared field by field the same way.
//   * Tests are never removed and the patient's status is never changed here.

import { nameTokens, normalizeDob } from "./patientDuplicateMatch";
import { mrn9 } from "./mrn";
import { PFT_TEST_VALUE_KEYS, testDateKey, testsFromRecord } from "./pftTests";
import { collapseSameReports, importToTest } from "./pftImportGroups";
import { withoutSoftCheck } from "./pftSoftChecks";

const blank = (v) => v === undefined || v === null || String(v).trim() === "";
const str = (v) => (v === undefined || v === null ? "" : String(v).trim());

// Profile fields the merge can touch. `get` reads the existing record (Sheet
// patients use `fullName`); `identity` fields are shown first and called out.
export const MERGE_PROFILE_FIELDS = [
  { field: "name", label: "Name", identity: true, get: (r) => r.name || r.fullName },
  { field: "dob", label: "Date of birth", identity: true, get: (r) => r.dob },
  { field: "mrn", label: "MRN", identity: true, get: (r) => r.mrn },
  { field: "phone", label: "Phone", get: (r) => r.phone },
  { field: "physicalAddress", label: "Address", get: (r) => r.physicalAddress },
  { field: "sex", label: "Sex", get: (r) => r.sex },
  { field: "smokingStatus", label: "Smoking status", get: (r) => r.smokingStatus },
  { field: "smokingNote", label: "Smoking status note", get: (r) => r.smokingNote },
];

const SEX = { m: "male", male: "male", f: "female", female: "female" };
const phoneDigits = (v) => str(v).replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");
const num = (v) => (/^-?\d+(\.\d+)?$/.test(str(v)) ? Number(str(v)) : null);

/** Same value, allowing for formatting differences per field. */
export const sameFieldValue = (field, a, b) => {
  if (blank(a) && blank(b)) return true;
  if (blank(a) || blank(b)) return false;
  switch (field) {
    case "name":
      return nameTokens(a).sort().join(" ") === nameTokens(b).sort().join(" ");
    case "dob":
    case "date": {
      const da = normalizeDob(a);
      return !!da && da === normalizeDob(b);
    }
    case "mrn":
      return !!mrn9(a) && mrn9(a) === mrn9(b);
    case "phone":
      return phoneDigits(a) === phoneDigits(b);
    case "sex": {
      const x = SEX[str(a).toLowerCase()];
      return !!x && x === SEX[str(b).toLowerCase()];
    }
    default: {
      const na = num(a);
      const nb = num(b);
      if (na !== null && nb !== null) return na === nb; // "71" = "71.0"
      return str(a).replace(/\s+/g, " ").toLowerCase() === str(b).replace(/\s+/g, " ").toLowerCase();
    }
  }
};

const kindOf = (field, current, incoming) => {
  if (blank(incoming)) return "keep"; // nothing extracted: leave it
  if (sameFieldValue(field, current, incoming)) return "same";
  if (blank(current)) return "fill";
  return "conflict";
};

/**
 * What the card brings: profile values plus the tests still waiting to move.
 * survey: the patient's health-survey answers, when they answered (decided 2026-10-23):
 * the phone, address and email they typed are offered instead of the PFT's ("Use
 * survey", ticked by default); a blank answer keeps the PFT's value. `sources` says
 * which fields came from the survey. Without a survey this is exactly as before.
 */
export const incomingFromCard = (card, survey = null) => {
  const incoming = {
    profile: {
      name: card.name,
      dob: card.dob,
      mrn: card.mrn9,
      phone: card.phone,
      physicalAddress: card.address,
      sex: card.sex,
      smokingStatus: card.smokingStatus,
      smokingNote: card.smokingNote,
    },
    tests: collapseSameReports((card.pendingImports || []).map(importToTest)),
  };
  if (!survey) return incoming;
  const sources = {};
  for (const [field, answer] of [["phone", survey.phone], ["physicalAddress", survey.address], ["email", survey.email]]) {
    if (blank(answer)) continue;
    incoming.profile[field] = str(answer);
    sources[field] = "survey";
  }
  return { ...incoming, sources };
};

// Email is only ever offered from the health survey (a PFT has none), so its row
// exists only then: a merge without a survey shows exactly the rows it always did.
const EMAIL_FIELD = { field: "email", label: "Email", get: (r) => r.email };

/**
 * Compare an existing patient record with what a card brings.
 * Returns {
 *   profile: [{ field, label, identity, current, incoming, kind }]
 *   tests:   [{ incoming, kind: "new" | "same-date" | "already",
 *               existingId?, fields?: [{ field, current, incoming, kind }] }]
 *   conflicts: number of choices the person must make
 * }
 * kind: "same" | "fill" (empty -> filled) | "conflict" (person picks) | "keep".
 */
export const planMerge = (record, incoming) => {
  const sources = incoming.sources || {};
  const fields = sources.email ? [...MERGE_PROFILE_FIELDS, EMAIL_FIELD] : MERGE_PROFILE_FIELDS;
  const profile = fields.map(({ field, label, identity, get }) => {
    const current = get(record || {});
    const value = (incoming.profile || {})[field];
    const row = { field, label, identity: !!identity, current: str(current), incoming: str(value), kind: kindOf(field, current, value) };
    // From the patient's own survey answer: offered as "Use survey", ticked by default.
    return sources[field] === "survey" ? { ...row, source: "survey", defaultChoice: "incoming" } : row;
  });

  const existing = testsFromRecord(record || {}).filter((t) => !t.removedAt);
  const tests = (incoming.tests || []).map((t) => {
    if (existing.some((e) => e.importId && e.importId === t.importId)) {
      return { incoming: t, kind: "already" };
    }
    const sameDay = testDateKey(t) && existing.find((e) => testDateKey(e) === testDateKey(t));
    if (!sameDay) return { incoming: t, kind: "new" };
    const fields = PFT_TEST_VALUE_KEYS.filter((k) => k !== "date").map((k) => ({
      field: k,
      current: str(sameDay[k]),
      incoming: str(t[k]),
      kind: kindOf(k, sameDay[k], t[k]),
    }));
    return { incoming: t, kind: "same-date", existingId: sameDay.id, fields };
  });

  const conflicts =
    profile.filter((r) => r.kind === "conflict").length +
    tests.reduce((n, t) => n + (t.fields || []).filter((f) => f.kind === "conflict").length, 0);
  return { profile, tests, conflicts };
};

/**
 * Apply a plan. `choices`:
 *   { profile: { [field]: "current" | "incoming" },
 *     tests:   { [incomingTestId]: { [field]: "current" | "incoming" } } }
 * A conflict without a choice keeps the current value.
 * Returns {
 *   profileUpdates  only the fields that change (fill, or a conflict set to incoming)
 *   tests           the patient's full test list after the merge (for pftFieldsForSave)
 *   addedTests, updatedTests, keptConflicts   counts, for the audit and the summary
 *   decidedSoftChecks  [{ testId, key }]: soft checks a person settled in this merge
 * }
 * by / now: who is merging, and when (stamped on the soft checks they settle).
 */
export const applyMerge = (record, plan, choices = {}, { by = "", now = new Date().toISOString() } = {}) => {
  const pick = (map, field) => (map && map[field]) || "current";
  const profileUpdates = {};
  let keptConflicts = 0;
  for (const r of plan.profile) {
    if (r.kind === "fill") profileUpdates[r.field] = r.field === "mrn" ? mrn9(r.incoming) || r.incoming : r.incoming;
    if (r.kind === "conflict") {
      // A survey answer is ticked by default (r.defaultChoice); everything else keeps RegX.
      if (((choices.profile && choices.profile[r.field]) || r.defaultChoice || "current") === "incoming") {
        profileUpdates[r.field] = r.field === "mrn" ? mrn9(r.incoming) || r.incoming : r.incoming;
      } else keptConflicts += 1;
    }
  }

  // Soft checks (pftSoftChecks.js) in a merge (decided 2026-10-20): a check comes along only
  // with a value that came from the PDF, and once a PERSON has chosen between two values
  // (a conflict: Keep RegX or Use PFT) nothing is left for them to verify, so the checks on
  // that field are marked settled by them (verifiedIn "merge"). Only a value filled in
  // automatically (RegX had none) keeps an open check.
  const decidedSoftChecks = [];
  const decided = (testId, key, sc) => {
    if (!sc || sc.verifiedAt) return sc;
    decidedSoftChecks.push({ testId, key });
    return { ...sc, verifiedBy: by, verifiedAt: now, verifiedIn: "merge" };
  };
  const addressRow = plan.profile.find((r) => r.field === "physicalAddress");
  const addressDecided = !!addressRow && addressRow.kind === "conflict";
  // An address the patient typed on the health survey isn't the PDF's: no soft check
  // comes with it.
  const addressFromPft =
    !!addressRow &&
    addressRow.source !== "survey" &&
    (addressRow.kind === "fill" || (addressDecided && pick(choices.profile, "physicalAddress") === "incoming"));
  const withAddressCheck = (test, sc) => ({
    ...test,
    softChecks: { ...(test.softChecks || {}), address: addressDecided ? decided(test.id, "address", sc) : sc },
  });
  const tests = testsFromRecord(record || {});
  let addedTests = 0;
  let updatedTests = 0;
  for (const t of plan.tests) {
    if (t.kind === "already") continue;
    if (t.kind === "new") {
      const softAddress = (t.incoming.softChecks || {}).address;
      const added = withoutSoftCheck({ ...t.incoming }, "address");
      tests.push(addressFromPft && softAddress ? withAddressCheck(added, softAddress) : added);
      addedTests += 1;
      continue;
    }
    const idx = tests.findIndex((e) => e.id === t.existingId);
    if (idx < 0) continue;
    const updated = { ...tests[idx] };
    let changed = false;
    const testChoices = (choices.tests || {})[t.incoming.id];
    for (const f of t.fields) {
      if (f.kind === "fill" || (f.kind === "conflict" && pick(testChoices, f.field) === "incoming")) {
        updated[f.field] = t.incoming[f.field];
        changed = true;
        const soft = (t.incoming.softChecks || {})[f.field];
        if (soft) {
          updated.softChecks = {
            ...(updated.softChecks || {}),
            [f.field]: f.kind === "conflict" ? decided(updated.id, f.field, soft) : soft,
          };
        }
      } else if (f.kind === "conflict") {
        keptConflicts += 1;
        // The person kept the value already there: its own soft check is settled too.
        const own = (updated.softChecks || {})[f.field];
        if (own && !own.verifiedAt) updated.softChecks = { ...updated.softChecks, [f.field]: decided(updated.id, f.field, own) };
      }
    }
    const softAddress = (t.incoming.softChecks || {}).address;
    if (addressFromPft && softAddress) Object.assign(updated, withAddressCheck(updated, softAddress));
    // Remember which upload this date came from, so the PDF can be opened.
    updated.mergedImportIds = [...new Set([...(updated.mergedImportIds || []), t.incoming.importId])];
    if (!updated.file && t.incoming.file) {
      updated.file = t.incoming.file;
      updated.fileName = t.incoming.fileName;
    }
    tests[idx] = updated;
    if (changed) updatedTests += 1;
  }
  // The person chose the address: any open address check already on the patient (from an
  // earlier PFT) is settled by that choice too.
  if (addressDecided) {
    tests.forEach((t, i) => {
      const sc = (t.softChecks || {}).address;
      if (sc && !sc.verifiedAt) tests[i] = { ...t, softChecks: { ...t.softChecks, address: decided(t.id, "address", sc) } };
    });
  }
  return { profileUpdates, tests, addedTests, updatedTests, keptConflicts, decidedSoftChecks };
};
